import alt from 'components/Dispatcher';
import moment, {Moment} from 'moment';
import {DashboardDateRange, TopicRollupDateRange} from '@store/@types/dash/DashboardDateRange';
import DashboardDateRangeActions from 'actions/dashboard/DasbhoardDateRangeActions';
import {Store} from '@store/@types/alt';

export type TopicRollupDateRanges = Record<TopicRollupDateRange, DashboardDateRange>;

// TODO switch dashboard from moment to day.js
interface DashboardDateRangeState extends DashboardDateRange {
  dashboardEndDate: Moment;
  topicRollupDateRanges: TopicRollupDateRanges;
  isTopicRollup: boolean;
}

interface TopicRollupSearchResult {
  found: boolean;
  range: DashboardDateRange;
}

/**
 * Stores the date range used by the dashboard date range selector.
 *
 * Do not use the state directly. Instead, use the functions below to access date ranges.
 */
class DashboardDateRangeStore extends Store<DashboardDateRangeState> {
  constructor() {
    super();

    // startDate = yesterday - 1 month.
    // endDate = yesterday.
    // exclusiveEndDate = today.
    const {startDate, endDate, exclusiveEndDate} = defaultDateRange();

    this.state = {
      startDate,
      endDate,
      dashboardEndDate: exclusiveEndDate,
      topicRollupDateRanges: topicRollupDateRanges(startDate, endDate, exclusiveEndDate),
      isTopicRollup: false,
    };
    this.bindActions(DashboardDateRangeActions);
  }

  /**
   * Set up for topic rollup:
   * 1. Ensure the dates are valid for the dropdown.
   * 2. Set the topic rollup flag.
   */
  enterTopicRollup(): void {
    const {found, range} = topicRollupHeaderRangeOrDefault(this.state);
    this.setState({
      isTopicRollup: true,
    });
    // If current range isn't valid, set it to one that is.
    if (!found) {
      this.setDateRange(range);
    }
  }

  /** Clear the topic rollup flag. */
  leaveTopicRollup(): void {
    this.setState({
      isTopicRollup: false,
    });
  }

  /**
   * Set the new date range.
   *
   * @param range a range with an exclusive end date.
   */
  setDateRange(range: DashboardDateRange): void {
    this.setState({
      startDate: range.startDate,
      endDate: range.endDate,
      dashboardEndDate: range.endDate.clone().add(1, 'day'),
    });
  }
}

/**
 * Get the stored date range to be used by dashboard components.
 *
 * This is the representation of the current range that will be sent to the backend when requesting data.
 *
 * @param state the current state of the store.
 * @return a range with an exclusive end date.
 */
export function dateRangeForDashboard(state: DashboardDateRangeState): DashboardDateRange {
  return {
    startDate: state.startDate,
    endDate: state.dashboardEndDate,
  };
}

/**
 * Get the stored date range to be used by the date range selector.
 *
 * This is the representation of the current range that will be displayed to the user.
 *
 * @param state the current state of the store.
 * @return a range with an inclusive end date.
 */
export function dateRangeForHeader(state: DashboardDateRangeState): DashboardDateRange {
  return {
    startDate: state.startDate,
    endDate: state.endDate,
  };
}

interface DefaultDateRange {
  startDate: Moment;
  endDate: Moment;
  exclusiveEndDate: Moment;
}

/**
 * Construct the default date range ([yesterday - 1 month, yesterday]), plus the end date for display to users (today).
 *
 * The store uses this internally for initialization.
 */
function defaultDateRange(): DefaultDateRange {
  const exclusiveEndDate = moment().startOf('day');
  const startDate = moment().subtract(1, 'month').startOf('day');
  const endDate = exclusiveEndDate.clone().subtract(1, 'day');

  return {
    startDate,
    endDate,
    exclusiveEndDate,
  };
}

/**
 * Find an exact match for the current date range among the topic rollup options, if one exists. If not, return the
 * default range (last month).
 *
 * @param state store state.
 * @return the topic rollup date range corresponding to the current date range in the store or the default, along with
 * a boolean indicating whether the default is being returned (i.e., whether the range in the store is valid for topic
 * rollups).
 */
function topicRollupHeaderRangeOrDefault(state: DashboardDateRangeState): TopicRollupSearchResult {
  const {startDate, endDate, topicRollupDateRanges} = state;
  const maybeMatch = Object.values(topicRollupDateRanges).find(range => {
    return range.startDate.isSame(startDate) && range.endDate.isSame(endDate);
  });

  const found = !!maybeMatch;
  // Fall back to one month if the dates don't match (e.g., if the user set a custom range on another page in the
  // dashboard).
  const range = !!maybeMatch ? maybeMatch : topicRollupDateRanges[TopicRollupDateRange.LastMonth];
  return {found, range};
}

/** Get a range representing the full quarter prior to the given date. */
function lastQuarter(endDate: Moment): DashboardDateRange {
  const lastQuarter = endDate.quarter() - 1;

  let startOfLastQuarter;
  if (lastQuarter === 0) {
    // Quarter starting October 1 of previous year.
    startOfLastQuarter = moment([endDate.year() - 1, 10, 1]);
  } else {
    const beginningMonthOfLastQuarter = lastQuarter * 3 - 2;
    startOfLastQuarter = moment([endDate.year(), beginningMonthOfLastQuarter - 1, 1]);
  }
  return {
    startDate: startOfLastQuarter,
    endDate: startOfLastQuarter.clone().add(3, 'months').subtract(1, 'day'),
  };
}

/**
 * Get date ranges for the topic rollup dropdown.
 *
 * These are all inclusive (i.e., they're what the header will display if the user switches pages).
 */
function topicRollupDateRanges(
  startDate: Moment,
  endDate: Moment,
  today: Moment
): TopicRollupDateRanges {
  const threeMonthsAgo = today.clone().subtract(3, 'months');

  return {
    [TopicRollupDateRange.LastMonth]: {startDate, endDate},
    [TopicRollupDateRange.Last3Months]: {startDate: threeMonthsAgo, endDate},
    [TopicRollupDateRange.LastQuarter]: lastQuarter(endDate),
  };
}

export default alt.createStore(DashboardDateRangeStore);
